import { Alert, Button, Divider, Grid, Stack, TextField, Typography } from "@mui/material";
import { RHFDateField, RHFDateTimeField, RHFRadioGroup, RHFSearchableSelectField, RHFTextField } from "../../common/hook-form";
import React, { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import dayjs from "dayjs";
import { LoadingButton } from "@mui/lab";
import _ from "lodash";
import { getMerchant } from "../../helpers/ReduxHelpers";
import { filterByProp } from "../../helpers/UtilityHelper";
import { useGetAllCourierPartnersQuery, useGetPtlAllowedCouriersQuery, useGetMerchantDetailsQuery, useGetMerchantQuery } from "../../redux/commonRTK";

const PHONE_REGEX = /^[0-9]{10}$/;
const INVOICE_VALUE_FOR_EWAY_BILL = 50000;

function OtherDetails({ setActiveStep, creatingOrder, extraDocs = {}, setExtraDocs = () => { }, showDocErrors = false }) {
    const { watch, setValue } = useFormContext()

    const setFile = (key) => (e) => {
        const file = e.target.files?.[0] || null;
        setExtraDocs((prev) => ({ ...prev, [key]: file }));
    };
    const setContact = (key) => (e) => {
        const val = (e.target.value || '').replace(/\D/g, '').slice(0, 10);
        setExtraDocs((prev) => ({ ...prev, [key]: val }));
    };
    const contact1Error = !!extraDocs.altContact1 && !PHONE_REGEX.test(extraDocs.altContact1);
    const contact2Error = !!extraDocs.altContact2 && !PHONE_REGEX.test(extraDocs.altContact2);
    const contactsInvalid = contact1Error || contact2Error;

    const invoiceValue = watch('invoice_value');
    const ewayBillCopyRequired = Number(invoiceValue) > INVOICE_VALUE_FOR_EWAY_BILL;
    // Errors only surface after a submit attempt — not on initial render.
    const invoiceCopyError = showDocErrors && !extraDocs.invoice;
    const ewayCopyError = showDocErrors && ewayBillCopyRequired && !extraDocs.eway_bill;

    const paymentMode = watch('payment_mode');
    const courierId = watch('courier_id');
    const pickupRequestDate = watch('pickup_request_date');

    useEffect(() => {
        if ((courierId === "Bluedart_PRZ_5" || courierId === "B2B_MOVIN_INT") && !pickupRequestDate) {
            const defaultDateTime = dayjs().add(1, 'day').toDate();
            const id = setTimeout(() => {
                setValue('pickup_request_date', defaultDateTime, { shouldValidate: false, shouldDirty: false });
            }, 0);
            return () => clearTimeout(id);
        }
    }, [courierId, setValue, pickupRequestDate]);

    const currentUser = getMerchant();

    const requestPayload = {
        andfilter: [{ "field": "is_deleted", "operator": "eq", "value": '0' }],
        offset: 0,
        limit: 500,
        includes: "id,name,parent,prozoId"
    }

    const { data: merchant } = useGetMerchantDetailsQuery({ id: currentUser.id })
    const { data: allCouriers = [] } = useGetAllCourierPartnersQuery(requestPayload);

    const b2bCheapestCPList = merchant?.b2bCheapestCPList || [];

    const allottedCouriers = allCouriers.filter(co => b2bCheapestCPList.includes(co.prozoId)).map(co => ({
        label: co.name,
        value: co.prozoId
    }));

    // A courier picked by hand skips allocation (skipRule), so for merchants with checkServiceability on, hide the
    // couriers whose PTL Serviceability list rules this lane out. No zone-map check (a hand-picked courier never had
    // one), and a courier with no list is never hidden, so nothing changes until a list is uploaded.
    const { data: merchantWithLocations } = useGetMerchantQuery({ id: currentUser.id })
    const warehouseName = watch('warehouse_name');
    // same pickup locations as the Pickup step offers
    const pickupLocations = _.filter(filterByProp(merchantWithLocations?.pickup_details, "is_deleted") || [],
        { status: 'ACTIVE', address_type: 'PICKUP', shipmentType: 'B2B' });
    const fromPincode = _.find(pickupLocations, { name: warehouseName })?.pincode;
    const toPincode = watch('delivery_details')?.to_pincode;
    const allottedIds = _.uniq(allottedCouriers.map(co => co.value)).sort().join(',');
    const checkLane = merchantWithLocations?.checkServiceability === true && !!fromPincode && !!toPincode && !!allottedIds;
    // currentData: the answer for this lane only (data would still hold the previous lane while refetching)
    const { currentData: serviceableCouriers, isError: laneCheckFailed } = useGetPtlAllowedCouriersQuery(
        { from: fromPincode, to: toPincode, prozoIds: allottedIds },
        { skip: !checkLane }
    );
    const laneChecked = checkLane && !laneCheckFailed && Array.isArray(serviceableCouriers);
    const validCouries = !checkLane || laneCheckFailed
        ? allottedCouriers
        : laneChecked ? allottedCouriers.filter(co => serviceableCouriers.some(p => p.cpProzoId === co.value)) : [];
    const hiddenCourierCount = laneChecked ? _.uniqBy(allottedCouriers, 'value').length - _.uniqBy(validCouries, 'value').length : 0;

    useEffect(() => {
        if (laneChecked && courierId && !_.some(validCouries, { value: courierId })) {
            setValue('courier_id', '');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [courierId, serviceableCouriers]);

    // React.useEffect(() => {
    //     resetField('courier_id');
    // }, [askForCourier])

    console.log(courierId);

    

    return (
        <Grid container spacing={3}>
            <Grid item xs={12}>
                <Typography variant="caption" fontWeight={500}><strong>Note:</strong> If the invoice value exceeds 50,000, kindly furnish the E-waybill number along with its expiration date.</Typography>
            </Grid>
            <Grid item xs={3}>
                <RHFRadioGroup
                    label="Payment Mode"
                    name="payment_mode"
                    required
                    options={[
                        { label: 'PREPAID', value: 'PREPAID' },
                        { label: 'COD', value: 'COD' },
                    ]}
                />
            </Grid>

            <Grid item xs={3}>
                <RHFRadioGroup
                    label="Insured"
                    name="insured"
                    required
                    options={[
                        { label: 'Yes', value: "true" },
                        { label: 'No', value: "false" },
                    ]}
                />
            </Grid>

            <Grid item xs={3}>
                <RHFRadioGroup
                    label="Appointment Delivery"
                    name="appointment_delivery"
                    required
                    options={[
                        { label: 'Yes', value: "true" },
                        { label: 'No', value: "false" },
                    ]}
                />
            </Grid>
            <Grid item xs={3}>
                <RHFRadioGroup
                    label="Risk Type"
                    name="riskType"
                    required
                    options={[
                        { label: 'Owner', value: "OWNER" },
                        { label: 'Carrier', value: "CARRIER" },
                    ]}
                />
            </Grid>
            {(paymentMode === 'COD') && (
                <Grid item xs={4}>
                    <RHFTextField name="cod_amount" label="COD Amount" type="number" required variant="filled" autoFocus />
                </Grid>
            )}
            <Grid item xs={4}>
                <RHFTextField name="ewaybill" label="Ewaybill Serial Number" variant="filled" autoFocus />
            </Grid>

            <Grid item xs={4}>
                <RHFDateField name="ewb_exp_date" label="Ewaybill Expiry Date" />
            </Grid>
            <Grid item xs={6}>
                <RHFSearchableSelectField
                    options={validCouries}
                    width="100%"
                    label="Please Select Courier"
                    name="courier_id"
                    mt={0}
                    mb={0}
                />
                {hiddenCourierCount > 0 && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                        {hiddenCourierCount} courier(s) hidden: not serviceable for {fromPincode} → {toPincode}.
                        Leave the courier empty to let allocation pick one.
                    </Alert>
                )}
            </Grid>
            {(courierId === "Bluedart_PRZ_5" || courierId === "B2B_MOVIN_INT") &&
                <Grid item xs={6}>
                    <RHFDateTimeField
                        width="100%"
                        label="Pickup Request Date and Time"
                        name="pickup_request_date"
                        mode="dateTime"
                        minDateTimeFromNowHours={1}
                    />
                     <Typography variant="caption" color="red" display="block" sx={{ mt: 0.5 }}>
                        Please provide pickup request date and time for Bluedart and Movin.
                    </Typography>
                </Grid>
            }
            <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" fontWeight={600}>Documents & Receiver Contact</Typography>
            </Grid>

            <Grid item xs={4}>
                <Typography variant="caption" display="block" sx={{ mb: 0.5, fontWeight: 600 }}>Invoice Copy *</Typography>
                <Button variant="outlined" size="small" component="label" color={invoiceCopyError ? 'error' : 'primary'}>
                    {extraDocs.invoice ? 'Replace File' : 'Choose File'}
                    <input type="file" hidden accept=".pdf,.jpg,.jpeg,.png" onChange={setFile('invoice')} />
                </Button>
                {extraDocs.invoice && (
                    <Typography variant="caption" noWrap display="block" title={extraDocs.invoice.name} sx={{ mt: 0.5, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {extraDocs.invoice.name}
                    </Typography>
                )}
                {invoiceCopyError && (
                    <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
                        Invoice copy is required
                    </Typography>
                )}
            </Grid>

            <Grid item xs={4}>
                <Typography variant="caption" display="block" sx={{ mb: 0.5, fontWeight: 600 }}>
                    E-Way Bill Copy{ewayBillCopyRequired ? ' *' : ''}
                </Typography>
                <Button variant="outlined" size="small" component="label" color={ewayCopyError ? 'error' : 'primary'}>
                    {extraDocs.eway_bill ? 'Replace File' : 'Choose File'}
                    <input type="file" hidden accept=".pdf,.jpg,.jpeg,.png" onChange={setFile('eway_bill')} />
                </Button>
                {extraDocs.eway_bill && (
                    <Typography variant="caption" noWrap display="block" title={extraDocs.eway_bill.name} sx={{ mt: 0.5, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {extraDocs.eway_bill.name}
                    </Typography>
                )}
                {ewayCopyError && (
                    <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
                        E-Way Bill copy is required for invoices &gt; Rs. 50,000
                    </Typography>
                )}
            </Grid>

            <Grid item xs={4} />

            <Grid item xs={4}>
                <TextField
                    fullWidth
                    size="small"
                    variant="filled"
                    label="Receiver Contact 1"
                    sx={{ fontSize: 14 }}
                    InputProps={{ sx: { fontSize: 12 } }}
                    InputLabelProps={{ sx: { fontSize: 10 } }}
                    value={extraDocs.altContact1 || ''}
                    onChange={setContact('altContact1')}
                    error={contact1Error}
                    helperText={contact1Error ? 'Must be 10 digits' : ''}
                />
            </Grid>
            <Grid item xs={4}>
                <TextField
                    fullWidth
                    size="small"
                    variant="filled"
                    label="Receiver Contact 2"
                    sx={{ fontSize: 14 }}
                    InputProps={{ sx: { fontSize: 12 } }}
                    InputLabelProps={{ sx: { fontSize: 10 } }}
                    value={extraDocs.altContact2 || ''}
                    onChange={setContact('altContact2')}
                    error={contact2Error}
                    helperText={contact2Error ? 'Must be 10 digits' : ''}
                />
            </Grid>

            <Grid item xs={12} sx={{ mt: 2 }}>
                <Stack direction="row" columnGap={1}>
                    <Button disabled={creatingOrder} variant="contained" size="small" color="error" onClick={() => setActiveStep(2)}>Prev</Button>
                    <LoadingButton variant="contained" size="small" type="submit" loading={creatingOrder} disabled={contactsInvalid}>
                        Create Shipment
                    </LoadingButton>
                </Stack>
            </Grid>
        </Grid>
    )
}

export default OtherDetails;